import util from '../util';
import giftColorThresholds from '../GiftThresholds';
import Widget from './Widget';
import './highlightedMessageWidget.scss';

export default class HighlightedMessageWidget extends Widget {
	constructor(opts) {
		super(opts);
		this._msgElem         = null;
		this._currentMsgId    = null;
		this._currentPrepared = null;
	}

	async init(initialData = {}) {
		if(window?.BroadcastChannel) {
			this._bc = new BroadcastChannel('LiveJoiner');
			this._bc.addEventListener('message', event => {
				if(event.data?.type === 'highlightedMessage')
					this._handleHighlight(event.data?.data);
			});
		}
		await super.init(initialData);
	}

	_build() {
		super._build();
		this.container.classList.add('hm-widget');
	}

	destroy() {
		this._bc?.close();
		document.getElementById('hmBaseCSS')?.remove();
		document.getElementById('hmStyle')?.remove();
		document.getElementById('googleFontLink')?.remove();
		super.destroy();
	}

	applyUserData(data) {
		super.applyUserData(data);
		if(data?.highlighted_message) this._handleHighlight(data.highlighted_message);
	}

	applySettings(settings) {
		const prevStyle = this.settings.style;
		super.applySettings(settings);
		this._updateWidgetClasses();
		if(this._msgElem && this._currentPrepared && this.settings.style !== prevStyle) {
			this.container.classList.remove(prevStyle);
			this.container.classList.add(this.settings.style);
			this.container.innerHTML = '';
			this._msgElem = this._buildMessageElem(this._currentPrepared, this.settings.style || 'bubble');
			this.container.appendChild(this._msgElem);
		}
	}

	_generateCSS() {
		const id = this.container.id, s = this.settings;

		const textDecoration = [
			s.underlineText     ? 'underline'    : null,
			s.strikethroughText ? 'line-through' : null,
		].filter(Boolean).join(' ');

		return `
			.hm-widget#${id} {
				${s.duration      ? `--animate-duration: ${s.duration}ms;`      : ''}
				${s.titleBg       ? `--hm-title-bg:      ${s.titleBg};`         : ''}
				${s.titleColor    ? `--hm-title-color:   ${s.titleColor};`      : ''}
				${s.textBg        ? `--hm-text-bg:       ${s.textBg};`          : ''}
				${s.textColor     ? `--hm-text-color:    ${s.textColor};`       : ''}
				${s.giftBg        ? `--hm-gift-bg:       ${s.giftBg};`          : ''}
				${s.giftColor     ? `--hm-gift-color:    ${s.giftColor};`       : ''}
				${s.alertTitleBg    || s.titleBg    ? `--hm-alert-title-bg:    ${s.alertTitleBg || s.titleBg};`       : ''}
				${s.alertTitleColor || s.titleColor ? `--hm-alert-title-color: ${s.alertTitleColor || s.titleColor};` : ''}
				${s.paddingLeft   ? `padding-left:       ${s.paddingLeft}px;`   : ''}
				${s.paddingRight  ? `padding-right:      ${s.paddingRight}px;`  : ''}
				${s.paddingBottom ? `padding-top:        ${s.paddingBottom}px;` : ''}
				${s.paddingBottom ? `padding-bottom:     ${s.paddingBottom}px;` : ''}
				${s.fontFamily    ? `font-family:        '${s.fontFamily}';`    : ''}
				${s.fontSize      ? `font-size:          ${s.fontSize}px;`      : ''}
				${s.letterSpacing ? `letter-spacing:     ${s.letterSpacing}px;` : ''}
				${s.boldText      ? 'font-weight:        bold;'                 : ''}
				${s.italicText    ? 'font-style:         italic;'               : ''}
				${textDecoration  ? `text-decoration:    ${textDecoration};`    : ''}
				${s.uppercaseText ? 'text-transform:     uppercase;'            : ''}
			}
			${giftColorThresholds.map(t => `
				.hm-widget#${id}:not(.minimal) .hm-msg.gift.gift-${t.value} .hm-title { background-color: ${t.secondaryColor} !important; color: ${t.textColor} !important; }
				.hm-widget#${id}:not(.minimal) .hm-msg.gift.gift-${t.value} .hm-content { background-color: ${t.color} !important; color: ${t.textColor} !important; }
				.hm-widget#${id}.blocks  .hm-msg.gift.gift-${t.value} .hm-avatar-wrap::before { background-color: ${t.secondaryColor} !important; }
				.hm-widget#${id}.classic .hm-msg.gift.gift-${t.value} .hm-avatar-wrap { background-color: ${t.secondaryColor} !important; }
				.hm-widget#${id}.minimal .hm-msg.gift.gift-${t.value} { background-color: ${t.color} !important; color: ${t.textColor} !important; }
				.hm-widget#${id}.minimal .hm-msg.gift.gift-${t.value}::before { background-color: ${t.secondaryColor} !important; }
			`).join('\n')}
			${s.blurRadius > 0 || s.spreadRadius > 0 ? `.hm-widget#${id} .hm-msg { filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)}; }` : ''}
			${s.extraCSS || ''}
		`;
	}

	_updateWidgetClasses() {
		const va = this.settings.verticalAlign || 'bottom';
		const ha = this.settings.textAlign     || 'left';
		this.container.className = `widget hm-widget va-${va} ha-${ha} ${this.settings.style || 'bubble'}`;
	}

	_handleHighlight(message) {
		if(!message) {
			if(this._msgElem) {
				const el = this._msgElem;
				this._msgElem = null;
				this._currentMsgId = null;
				this._currentPrepared = null;
				this._startExit(el);
			}
			return;
		}
		const prepared = util.prepareMessage(message.message || message, {
			id: true, serviceId: true, serviceName: true, serviceIconUrl: true,
			avatarUrl: true, displayName: true, spansHtml: true,
			giftString: true, eventString: true, classList: true, giftColorThreshold: true,
			images: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
			giftColorThresholds,
			giftMediaThresholds: this.giftThresholds,
			textReplacements: this.textReplacements,
		});

		if(this._msgElem && prepared.id && prepared.id === this._currentMsgId) {
			// already showing — nothing to do, server manages expiry
		} else {
			this._showMessage(prepared);
		}
	}

	_showMessage(prepared) {
		const msgElem = this._buildMessageElem(prepared, this.settings.style || 'bubble');
		const oldElem = this._msgElem;

		this._msgElem = msgElem;
		this._currentMsgId = prepared.id || null;
		this._currentPrepared = prepared;

		if(oldElem) this._startExit(oldElem);

		// Stack above any elements still playing exit animations
		if(this.container.children.length > 0) {
			msgElem.style.position = 'relative';
			msgElem.style.zIndex   = '2';
		}
		this.container.appendChild(msgElem);

		if(this.settings.inTransition) {
			msgElem.classList.add('animate__animated', `animate__${this.settings.inTransition}`);
			msgElem.addEventListener('animationend', () => {
				msgElem.classList.remove('animate__animated', `animate__${this.settings.inTransition}`);
				msgElem.style.position = '';
				msgElem.style.zIndex   = '';
			}, { once: true });
		} else {
			msgElem.style.position = '';
			msgElem.style.zIndex   = '';
		}
	}

	// Pins el at its current rendered position and plays the exit animation (or removes immediately).
	// Cancels any in-progress enter animation first so the element exits from where it currently is.
	// Each element calls this at most once — no global "hiding" flag needed.
	_startExit(el) {
		if(!el) return;
		if(this.settings.inTransition)
			el.classList.remove('animate__animated', `animate__${this.settings.inTransition}`);
		if(el.style.position !== 'absolute') {
			const top = el.offsetTop, left = el.offsetLeft, width = el.offsetWidth, height = el.offsetHeight;
			el.style.position = 'absolute';
			el.style.top    = top    + 'px';
			el.style.left   = left   + 'px';
			el.style.width  = width  + 'px';
			el.style.height = height + 'px';
		}
		el.style.zIndex = '1';
		if(this.settings.outTransition) {
			el.classList.add('animate__animated', `animate__${this.settings.outTransition}`);
			el.addEventListener('animationend', () => el.remove(), { once: true });
		} else {
			el.remove();
		}
	}

	/* ── DOM builders ──────────────────────────────────────── */

	_buildMessageElem(prepared, style) {
		const div = document.createElement('div');
		const classes = (prepared.classList || []).filter(c => c !== 'message');
		div.className = ['hm-msg', ...classes].join(' ');
		switch(style) {
			case 'blocks':  this._buildBlocks(div, prepared);  break;
			case 'minimal': this._buildMinimal(div, prepared); break;
			case 'classic': this._buildClassic(div, prepared); break;
			default:        this._buildBubble(div, prepared);  break;
		}
		return div;
	}

	_buildBubble(div, prepared) {
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildAvatarWrap(prepared));
		title.appendChild(this._buildSenderP(prepared));
		div.appendChild(title);
		div.appendChild(this._buildContent(prepared));
	}

	_buildBlocks(div, prepared) {
		div.appendChild(this._buildAvatarWrap(prepared));
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildSenderP(prepared));
		div.appendChild(title);
		div.appendChild(this._buildContent(prepared));
	}

	_buildMinimal(div, prepared) {
		div.appendChild(this._buildAvatarWrap(prepared));
		const textArea = document.createElement('div');
		textArea.className = 'hm-text-area';
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildSenderP(prepared));
		textArea.appendChild(title);
		textArea.appendChild(this._buildContent(prepared));
		div.appendChild(textArea);
	}

	_buildClassic(div, prepared) {
		div.appendChild(this._buildAvatarWrap(prepared));
		const textWrap = document.createElement('div');
		textWrap.className = 'hm-text-wrap';
		const title = document.createElement('div');
		title.className = 'hm-title';
		title.appendChild(this._buildSenderP(prepared));
		textWrap.appendChild(title);
		textWrap.appendChild(this._buildContent(prepared));
		div.appendChild(textWrap);
	}

	_buildAvatarWrap(prepared) {
		const wrap = document.createElement('div');
		wrap.className = 'hm-avatar-wrap';

		if(prepared.avatarUrl) {
			const img = document.createElement('img');
			img.src = prepared.avatarUrl;
			img.alt = '';
			wrap.appendChild(img);
		} else {
			const fallback = document.createElement('div');
			fallback.className = 'hm-avatar-fallback';
			fallback.innerHTML = `
				<svg fill="#eee9f0" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
					<path d="m 256,17.712357 c 60.75077,0 109.99959,40.491541 109.99959,112.288053 0,71.79651 -49.24882,130.00041 -109.99959,130.00041 -60.75077,0 -109.99959,-58.2039 -109.99959,-130.00041 C 146.00041,58.203898 195.24923,17.712357 256,17.712357 Z M 442.19077,512 H 69.809232 c -18.67159,0 -33.808411,-15.13682 -33.808411,-33.80841 0,-87.36657 98.495999,-188.6034 219.999179,-188.6034 121.50318,0 219.99918,101.23683 219.99918,188.6034 C 475.99918,496.86318 460.864,512 442.19077,512 Z"/>
				</svg>
			`;
			wrap.appendChild(fallback);
		}

		if(prepared.serviceIconUrl) {
			const si = document.createElement('img');
			si.src = prepared.serviceIconUrl;
			si.alt = prepared.serviceName || '';
			si.className = 'hm-service-icon';
			wrap.appendChild(si);
		}

		return wrap;
	}

	_buildSenderP(prepared) {
		const p = document.createElement('p');
		p.className = 'hm-sender';
		p.textContent = [prepared.displayName, prepared.giftString, prepared.eventString]
			.filter(Boolean).join(' ');
		return p;
	}

	_buildContent(prepared) {
		const div = document.createElement('div');
		div.className = 'hm-content';
		div.innerHTML = prepared.spansHtml || '';
		// prepareMessage already sizes `images` to the gift's own multiplier (the same
		// cascade the chat widget/log use), so requesting it is enough to pick up the
		// total — nothing multiplier-specific needed here beyond rendering the result.
		const urls = (prepared.images || []).filter(img => img?.url).map(img => img.url);
		if(urls.length) util.placeChatImages(div, urls);
		return div;
	}
}
